import { useMemo } from 'react';

import type { NativeReportColumn, NativeReportRow } from '@/api/reports';
import { TableCard } from '@/lib/base/table/table';
import type { ChartDatapointSelection } from '@/types/reports';
import { cx } from '@/utils/cx';

import { sourceRowIndexOf } from './charts/chartSelection';
import { formatCellValue } from './constants';
import { ReportTableCellContent } from './ReportTableCell';

/**
 * Identity stamp added to display rows by `sortTableDataChronologically` when it
 * reorders them. It is a row-identity marker, never a data column, so it is
 * excluded from the derived column list.
 */
const SOURCE_ROW_INDEX_KEY = '__sourceRowIndex';

/**
 * Shared tabular renderer for report results — used by both the saved-report
 * table view (ChartRenderer) and the wizard's live preview. One source of
 * truth for the markup and tokens so the two cannot drift apart, and so every
 * surface stays theme-aware (per-file copies of this table once hardcoded
 * `bg-white`, which broke the rows in dark mode).
 *
 * Layout is deliberately `auto`, not `fixed`: a report can return anywhere
 * from two columns to a dozen, and a fixed layout divides the width evenly
 * regardless — in a half-width dashboard tile that renders every column too
 * narrow to read. Auto sizing plus horizontal scroll keeps whatever fits
 * readable and lets the rest be reached.
 */

/** Sentinel emitted by the pivot transform for genuinely-null cells. */
const NULL_TOKEN = '∅';

/**
 * Column types whose values are magnitudes rather than labels.
 *
 * These get right-alignment and tabular figures, so digits line up in a
 * column and `$1,240` reads as obviously smaller than `$847,300` without
 * being read. Comparing magnitudes at a glance is most of why a number ends
 * up in a table, and left-aligned proportional digits defeat it.
 */
const NUMERIC_COLUMN_TYPES: ReadonlySet<string> = new Set([
  'number',
  'currency',
  'percentage',
]);

/**
 * Widest a text cell gets before it truncates.
 *
 * Long values (deal names, company names) previously set `whitespace-nowrap`
 * with no bound, so one long name pushed the table into horizontal scrolling
 * — which in a dashboard tile hides the columns to its right. Truncating with
 * the full value on `title` keeps the grid intact and the value reachable.
 */
const TEXT_CELL_CLASSES = 'max-w-[280px] truncate';

const HEAD_CELL_CLASSES =
  // `whitespace-nowrap` is load-bearing: without it a two-word header
  // ("Close Date (Day)") wraps to two lines and then clips mid-word at the
  // container edge, which reads as a rendering fault rather than a narrow
  // column. Letting the column size to its header instead keeps the label
  // whole — the scroll region already exists to reach it.
  //
  // Sticky lives on the cells, not on <thead>: a sticky <thead> drops its
  // bottom border in Safari and Firefox, so the header bleeds into the first
  // row once you scroll. The border goes on the cells for the same reason.
  //
  // The background matches the surface rather than tinting it: an opaque fill
  // is required for sticky (rows scroll *under* the header), but a grey band
  // across a white card is a heavy box the data has to fight. The bottom
  // border alone does the separating.
  'sticky top-0 z-10 whitespace-nowrap border-b border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] px-4 py-2.5 text-xs font-medium text-[var(--color-text-secondary)]';

// `py-2` (8px), not a rounder-looking `py-2.5`, because the number is borrowed
// rather than chosen: it is what the CRM list's cells use, and a report grouped
// by company should sit at the same rhythm as the Companies list it mirrors.
// Measured, both at 24px avatar + 14px/20px text: CRM list rows land at 42px,
// and this table now lands within a pixel of that. At 10px the same row was
// 47px, which read as loose next to it.
const BODY_CELL_CLASSES = 'px-4 py-2 text-sm text-[var(--color-text-primary)]';

/**
 * Everything the table needs to make cells actionable. All-or-nothing on
 * purpose: a surface opts in by supplying it and opts out by omitting it, the
 * same way the chart cases opt out of drill by not passing `onDatapointSelect`.
 * That is why the public share page and the dynamic-views chart stay inert here
 * without a flag, a permission check, or a single branch of their own.
 */
export interface ReportTableInteraction {
  /** Needed to resolve entity cells to their record. */
  orgId: number;
  /** Report columns — `is_aggregation` marks which cells are measures. */
  columns: NativeReportColumn[];
  /**
   * Native rows, positionally aligned with `rows`; carries each row's
   * `group_raw`.
   *
   * The alignment is why a drill from row `i` can name native row `i`, and it
   * holds because `nativeToTablePivot` is a 1:1 order-preserving map and the
   * table — unlike the charts — never reorders or truncates. The charts stamp
   * `__sourceRowIndex` precisely because `applyTopN` breaks that property for
   * them. Sorting or filtering rows here would break it too, and would need
   * the same stamp rather than this index.
   */
  nativeRows: NativeReportRow[];
  /** Opens the drill panel for the clicked row. */
  onDrill: (selection: ChartDatapointSelection) => void;
  /** The row currently drilled, so the table can mark it as the charts do. */
  activeSelection?: ChartDatapointSelection | null;
}

export interface ReportDataTableProps {
  rows: Record<string, unknown>[];
  /** Column key → human-readable title. Falls back to the raw key. */
  columnTitles: Record<string, string>;
  /** Column key → Cube.js column type (drives cell formatting). */
  columnTypes: Record<string, string>;
  resolveLabel: (value: string, dimensionName?: string) => string;
  currency: string;
  /**
   * Height budget in px — the same budget every other report renderer takes
   * as `height`.
   *
   * Required, deliberately. A report table renders one row per result row, so
   * unbounded it grows to whatever the query returned. On a dashboard that is
   * worse than it sounds: the grid uses `auto-rows-fr`, so a single 38-row
   * table stretched every other card in its row to match and a four-tile
   * dashboard measured 4306px for content that needs 750px. Every call site
   * already has a height in hand, so there is no case where "unbounded" is
   * the right answer and no default worth guessing — an optional prop here
   * would just be a silent fallback waiting to be forgotten again.
   */
  maxHeight: number;
  /** Omit to render a completely inert, fully selectable table. */
  interaction?: ReportTableInteraction;
}

export function ReportDataTable({
  rows,
  columnTitles,
  columnTypes,
  resolveLabel,
  currency,
  maxHeight,
  interaction,
}: ReportDataTableProps) {
  // One authoritative column list drives BOTH headers and cells: iterating
  // each row's own entries shifted values under the wrong headers whenever
  // a row was missing a key or carried an extra one. Union of all rows'
  // keys, in first-seen order.
  const columnKeys = useMemo(() => {
    const keys: string[] = [];
    const seen = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (key === SOURCE_ROW_INDEX_KEY) {
          continue;
        }
        if (!seen.has(key)) {
          seen.add(key);
          keys.push(key);
        }
      }
    }
    return keys;
  }, [rows]);

  // Resolved once per column rather than per cell — a 200-row table asks the
  // same question 200 times otherwise.
  const numericColumns = useMemo(() => {
    const numeric = new Set<string>();
    for (const key of columnKeys) {
      const type = columnTypes[key];
      if (type !== undefined && NUMERIC_COLUMN_TYPES.has(type)) {
        numeric.add(key);
      }
    }
    return numeric;
  }, [columnKeys, columnTypes]);

  // Measure vs dimension comes from the backend's own `is_aggregation` flag,
  // not from the cell's data type — a dimension can be numeric (grouping by
  // headcount), and guessing would make those cells drill when they shouldn't.
  // Absent means dimension, so an older response degrades to inert.
  const interactionColumns = interaction?.columns;
  const measureColumns = useMemo(() => {
    const measures = new Set<string>();
    for (const col of interactionColumns ?? []) {
      if (col.is_aggregation === true) {
        measures.add(col.key);
      }
    }
    return measures;
  }, [interactionColumns]);

  if (rows.length === 0) {
    return null;
  }

  return (
    // The design system's scroll area rather than a bare `overflow-auto`
    // div: it adds the edge-fade hints that tell the reader a clipped column
    // is scrollable rather than broken, which is the difference between a
    // narrow table looking deliberate and looking faulty. It also owns the
    // scroll region on both axes, so callers no longer wrap this in an
    // `overflow-x-auto` of their own.
    //
    // `style` is the one isolated home for the dynamic height
    // (FRONTEND_PATTERNS § Design System: no scattered inline styles; a
    // genuinely dynamic value lives in one place). Tailwind's arbitrary-value
    // syntax can't express it — the JIT never sees a runtime number.
    <TableCard.ScrollArea className='overflow-auto' style={{ maxHeight }}>
      <table className='min-w-full divide-y divide-[var(--color-border-secondary)]'>
        <thead>
          <tr>
            {columnKeys.map(key => (
              <th
                key={key}
                // A header that doesn't share its column's alignment leaves the
                // label floating away from the values it names.
                className={cx(
                  HEAD_CELL_CLASSES,
                  numericColumns.has(key) ? 'text-right' : 'text-left'
                )}
              >
                {columnTitles[key] || key}
              </th>
            ))}
          </tr>
        </thead>
        {/* Row separators only — the previous zebra striping ran *alongside*
            these borders, so two devices did one job and neither could be
            read as deliberate. Uniform rows also let the hover state land
            visibly, which striping washed out. */}
        <tbody className='divide-y divide-[var(--color-border-secondary)]'>
          {rows.map((row, index) => {
            // A chronological reorder (date-granularity primary group-by) breaks
            // the positional 1:1 with `nativeRows`, so resolve the ORIGINAL
            // native-row index from the `__sourceRowIndex` stamp when present.
            // Absent (unsorted path), the positional index still holds. This is
            // the index the drill panel resolves against `nativeData.rows`.
            const sourceRowIndex = sourceRowIndexOf(row) ?? index;
            const nativeRow = interaction?.nativeRows[sourceRowIndex];
            // Mirrors the drill panel's own guard: with no group descriptors
            // there is no segment to resolve, so nothing in the row can drill.
            // A report with measures but no group-by still ships `group_raw: {}`,
            // so an empty object must not expose the drill action either.
            const groupRawForRow = nativeRow?.group_raw;
            const canDrillRow =
              !!interaction &&
              !!groupRawForRow &&
              Object.keys(groupRawForRow).length > 0;
            const isActiveRow =
              interaction?.activeSelection?.rowIndex === sourceRowIndex;
            return (
              <tr
                // eslint-disable-next-line react/no-array-index-key -- Report rows lack stable identity; index is safe for static result data
                key={index}
                className={cx(
                  'transition-colors hover:bg-[var(--color-bg-tertiary)]',
                  // The drilled row stays marked while its panel is open, the
                  // same way the charts keep the clicked datapoint highlighted.
                  isActiveRow && 'bg-[var(--color-bg-tertiary)]'
                )}
                data-selected={isActiveRow || undefined}
              >
                {columnKeys.map(key => {
                  const value = key in row ? row[key] : null;
                  const display = formatCellValue(
                    value === NULL_TOKEN ? null : value,
                    key,
                    columnTypes[key],
                    resolveLabel,
                    currency
                  );
                  const isNumeric = numericColumns.has(key);
                  const groupRaw = nativeRow?.group_raw?.[key];
                  const entityId =
                    groupRaw?.kind === 'entity'
                      ? groupRaw.entity_id
                      : undefined;
                  const isMeasure = measureColumns.has(key);
                  return (
                    <td
                      key={key}
                      className={cx(
                        BODY_CELL_CLASSES,
                        isNumeric
                          ? // Numbers never wrap or truncate: a clipped figure
                            // is worse than no figure, and they are short.
                            'whitespace-nowrap text-right tabular-nums'
                          : TEXT_CELL_CLASSES
                      )}
                      // Only plain text can be cut off. An entity badge and a
                      // drill button carry their own labels, and a duplicate
                      // tooltip over them just fights the real one.
                      title={
                        isNumeric || entityId !== undefined || isMeasure
                          ? undefined
                          : display
                      }
                    >
                      <ReportTableCellContent
                        display={display}
                        rowIndex={sourceRowIndex}
                        columnKey={key}
                        isMeasure={isMeasure}
                        canDrillRow={canDrillRow}
                        entityId={entityId}
                        interaction={interaction}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableCard.ScrollArea>
  );
}
